import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send, CheckCircle2, AlertTriangle, LifeBuoy } from "lucide-react";

const STATUS_LABELS = {
  open: "Open", in_review: "In review", awaiting_user: "Awaiting your reply",
  resolved: "Resolved", closed: "Closed",
};
const STATUS_COLORS = {
  open: "bg-emerald-100 text-emerald-700 border-emerald-300",
  in_review: "bg-blue-100 text-blue-700 border-blue-300",
  awaiting_user: "bg-amber-100 text-amber-700 border-amber-300",
  resolved: "bg-slate-100 text-slate-600 border-slate-300",
  closed: "bg-slate-100 text-slate-500 border-slate-300",
};

export default function TicketDetail() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/support/tickets/${ticketId}`);
      setTicket(data.ticket);
      setMessages(data.messages || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load ticket"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // light polling for admin replies
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (reply.trim().length < 1) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${ticketId}/message`, { body: reply.trim() });
      setReply("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const closeTicket = async () => {
    if (!window.confirm("Close this ticket? You'll need to raise a new one for further help.")) return;
    try {
      await api.post(`/support/tickets/${ticketId}/close`);
      toast.success("Ticket closed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not close"); }
  };

  const adminSetStatus = async (status) => {
    try {
      await api.post(`/admin/support/tickets/${ticketId}/status`, { status });
      toast.success(`Ticket marked ${status.replace('_', ' ')}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
  };
  const isAdmin = user?.role === "admin";

  if (loading) return <div className="min-h-[50vh] grid place-items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!ticket) return <div className="min-h-[50vh] grid place-items-center text-slate-500">Ticket not found.</div>;

  const isClosed = ticket.status === "closed" || ticket.status === "resolved";

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-10" data-testid="ticket-detail-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Link to="/support/tickets" className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-800 text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to my tickets
        </Link>

        <div className="hw-card p-5 border-l-4 border-l-emerald-500">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`hw-chip border ${STATUS_COLORS[ticket.status]} capitalize`} data-testid="ticket-status">
              {STATUS_LABELS[ticket.status] || ticket.status}
            </span>
            <span className="hw-chip border-slate-300 bg-slate-50 text-slate-700 uppercase text-xs">{ticket.priority}</span>
            <span className="hw-chip border-slate-200 bg-white text-slate-600 capitalize text-xs">{ticket.category}</span>
            {ticket.is_emergency && (
              <span className="hw-chip border-red-300 bg-red-50 text-red-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Emergency</span>
            )}
          </div>
          <h1 className="hw-head text-2xl md:text-3xl font-black text-slate-900" data-testid="ticket-subject">{ticket.subject}</h1>
          <div className="hw-mono text-[11px] text-slate-500 mt-1">{ticket.ticket_id} · Opened {new Date(ticket.created_at).toLocaleString()}</div>
        </div>

        <div ref={scrollRef} className="hw-card mt-4 p-4 max-h-[520px] overflow-y-auto space-y-3" data-testid="ticket-messages">
          {messages.map((m) => {
            const mine = m.sender_id === user?.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-sm p-3 ${mine ? "bg-emerald-50 border border-emerald-200" : m.sender_role === "admin" ? "bg-blue-50 border border-blue-200" : "bg-slate-50 border border-slate-200"}`}>
                  <div className="flex items-center gap-2 text-xs mb-1">
                    <span className="hw-head font-black text-slate-900">{m.sender_name}</span>
                    {m.sender_role === "admin" && <span className="hw-chip border-blue-300 bg-blue-50 text-blue-700 text-[10px]">SwaanStay</span>}
                    <span className="text-slate-500">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{m.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        {isClosed ? (
          <div className="hw-card p-4 mt-4 border-l-4 border-l-slate-400 bg-slate-50 flex items-center gap-2 text-slate-700">
            <CheckCircle2 className="w-5 h-5 text-slate-500" />
            <div>This ticket is <span className="hw-head font-black">{STATUS_LABELS[ticket.status]}</span>. Raise a new ticket if you need more help.</div>
          </div>
        ) : (
          <div className="hw-card p-4 mt-4 space-y-3" data-testid="ticket-reply-box">
            {isAdmin && (
              <div className="flex flex-wrap gap-2 pb-3 border-b border-slate-200" data-testid="admin-status-controls">
                <span className="hw-label text-slate-500 self-center">Set status:</span>
                {[
                  ["in_review", "In review"],
                  ["awaiting_user", "Awaiting user"],
                  ["resolved", "Resolved"],
                  ["closed", "Closed"],
                ].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => adminSetStatus(k)}
                    disabled={ticket.status === k}
                    className={`hw-chip border ${ticket.status === k ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-300 text-slate-700 hover:border-slate-500"} disabled:opacity-50`}
                    data-testid={`admin-status-${k}`}
                  >{label}</button>
                ))}
              </div>
            )}
            <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)}
              placeholder={isAdmin ? "Reply as SwaanStay support…" : "Type your reply…"}
              className="rounded-sm" data-testid="ticket-reply-input" />
            <div className="flex gap-2 justify-between">
              {!isAdmin ? (
                <Button onClick={closeTicket} variant="outline"
                  className="border-slate-300 rounded-sm text-sm" data-testid="ticket-close-btn">
                  Close ticket
                </Button>
              ) : <div />}
              <Button onClick={send} disabled={sending || reply.trim().length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold" data-testid="ticket-send-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send reply
              </Button>
            </div>
          </div>
        )}

        <div className="mt-4 text-center text-xs text-slate-500">
          <LifeBuoy className="w-3 h-3 inline mr-1" />
          Our team typically responds within 24 hours. High-priority and emergency tickets are prioritised.
        </div>
      </div>
    </div>
  );
}
